import axios from 'axios';
import { useAuthStore } from '../store/authStore';
import type {
  Success, Page, CourseCard, Cohort, StudentRow, AnalyticsTopic, TopicStudentRow,
  ChapterRow, StudentTopicRow, ExerciseRow, AttemptRow, Measure, Explanation, TopicGlobal
} from '../types/teacherAnalytics';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const getToken = () => {
  return useAuthStore.getState().token;
};

export interface AnalyticsPageQuery { page?: number; limit?: number }
export interface AnalyticsStudentQuery extends AnalyticsPageQuery { q?: string }
const analyticsGet = async <T,>(path: string, params: object, signal?: AbortSignal): Promise<Success<T>> => {
  const response = await axios.get<Success<T>>(`${API_BASE_URL}/courses${path}`, {
    params, signal, headers: { Authorization: `Bearer ${getToken()}` }
  });
  return response.data;
};
const analyticsBase = (courseId: string) => `/${encodeURIComponent(courseId)}/analytics`;
const studentBase = (courseId: string, studentId: string) =>
  `${analyticsBase(courseId)}/students/${encodeURIComponent(studentId)}`;
const studentTopicBase = (courseId: string, studentId: string, topicId: string) =>
  `${studentBase(courseId, studentId)}/topics/${encodeURIComponent(topicId)}`;
export const getAnalyticsCourses = (q: AnalyticsPageQuery = {}, signal?: AbortSignal) =>
  analyticsGet<Page<CourseCard>>('/teacher-analytics', q, signal);
export const getCourseAnalytics = (courseId: string, signal?: AbortSignal) =>
  analyticsGet<{course: CourseCard; overview: Cohort}>(analyticsBase(courseId), {}, signal);
export const getAnalyticsStudents = (courseId: string, q: AnalyticsStudentQuery = {}, signal?: AbortSignal) =>
  analyticsGet<{course: CourseCard; students: Page<StudentRow>}>(`${analyticsBase(courseId)}/students`, q, signal);
export const getAnalyticsTopics = (courseId: string, q: AnalyticsPageQuery = {}, signal?: AbortSignal) =>
  analyticsGet<{courseId: string; topics: Page<AnalyticsTopic & {overview: Cohort}>}>(`${analyticsBase(courseId)}/topics`, q, signal);
export const getTopicAnalyticsStudents = (courseId: string, topicId: string, q: AnalyticsStudentQuery = {}, signal?: AbortSignal) =>
  analyticsGet<{courseId: string; topic: AnalyticsTopic; overview: Cohort; students: Page<TopicStudentRow>}>(
    `${analyticsBase(courseId)}/topics/${encodeURIComponent(topicId)}/students`, q, signal);
export const getStudentAnalytics = (courseId: string, studentId: string, signal?: AbortSignal) =>
  analyticsGet<{course: CourseCard; student: StudentRow}>(studentBase(courseId, studentId), {}, signal);
export const getStudentAnalyticsChapters = (courseId: string, studentId: string, q: AnalyticsPageQuery = {}, signal?: AbortSignal) =>
  analyticsGet<{courseId: string; studentId: string; chapters: Page<ChapterRow>}>(`${studentBase(courseId, studentId)}/chapters`, q, signal);
export const getStudentAnalyticsTopics = (courseId: string, studentId: string, q: AnalyticsPageQuery = {}, signal?: AbortSignal) =>
  analyticsGet<{courseId: string; studentId: string; topics: Page<StudentTopicRow>}>(`${studentBase(courseId, studentId)}/topics`, q, signal);
export const getStudentTopicExercises = (courseId: string, studentId: string, topicId: string, q: AnalyticsPageQuery = {}, signal?: AbortSignal) =>
  analyticsGet<{courseId: string; studentId: string; topicId: string; exercises: Page<ExerciseRow>}>(
    `${studentTopicBase(courseId, studentId, topicId)}/exercises`, q, signal);
export const getStudentTopicAttempts = (courseId: string, studentId: string, topicId: string,
  q: AnalyticsPageQuery & {activityId: string; exerciseId: string}, signal?: AbortSignal) =>
  analyticsGet<{courseId: string; studentId: string; topicId: string; activityId: string; exerciseId: string;
    attempts: Page<AttemptRow>; history: Measure}>(`${studentTopicBase(courseId, studentId, topicId)}/attempts`, q, signal);
export const getStudentTopicExplanations = (courseId: string, studentId: string, topicId: string, q: AnalyticsPageQuery = {}, signal?: AbortSignal) =>
  analyticsGet<{courseId: string; studentId: string; topicId: string; explanations: Page<Explanation>; topicGlobal: TopicGlobal}>(
    `${studentTopicBase(courseId, studentId, topicId)}/explanations`, q, signal);

export interface Course {
  _id: string;
  courseCode: string;
  title: string;
  description: string;
  teacher: string | {
    _id: string;
    name: string;
    email: string;
    teacherInfo?: any;
  };
  chapters: Chapter[];
  status: 'draft' | 'created' | 'published';
  isPublished: boolean;
  publishedAt?: string;
  enrolledStudents: string[] | StudentInfo[];
  totalTopics: number;
  estimatedDuration: number;
  createdAt: string;
  updatedAt: string;
  enrolledStudentsDetails?: StudentInfo[];
}

export interface Section {
  _id: string;
  title: string;
  description?: string;
  order: number;
  topics: string[];
}

export interface Chapter {
  _id: string;
  title: string;
  description?: string;
  order: number;
  sections?: Section[];
  topics: string[];
}

export interface CreateCourseData {
  title: string;
  description: string;
}

export interface UpdateCourseData {
  title?: string;
  description?: string;
  chapters?: Array<{
    _id?: string;
    title: string;
    description?: string;
    order: number;
    topics: string[];
  }>;
  isPublished?: boolean;
}

// Create a new course
export const createCourse = async (data: CreateCourseData): Promise<Course> => {
  const token = getToken();
  const response = await axios.post(
    `${API_BASE_URL}/courses`,
    data,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data.data;
};

// Replicate an existing course as a new draft
export const replicateCourse = async (courseId: string): Promise<Course> => {
  const token = getToken();
  const response = await axios.post(
    `${API_BASE_URL}/courses/${courseId}/replicate`,
    {},
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data.data;
};

// Get all courses
export const getCourses = async (filters?: {
  teacher?: string;
  published?: boolean;
  enrolled?: string;
}): Promise<Course[]> => {
  const token = getToken();
  const params = new URLSearchParams();
  if (filters?.teacher) params.append('teacher', filters.teacher);
  if (filters?.published !== undefined) params.append('published', filters.published.toString());
  if (filters?.enrolled) params.append('enrolled', filters.enrolled);

  const response = await axios.get(
    `${API_BASE_URL}/courses?${params.toString()}`,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data.data;
};

// Get course by ID
export const getCourseById = async (courseId: string): Promise<Course> => {
  const token = getToken();
  const response = await axios.get(
    `${API_BASE_URL}/courses/${courseId}`,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data.data;
};

// Get course by code
export const getCourseByCode = async (courseCode: string): Promise<{
  course: Course;
  isEnrolled: boolean;
}> => {
  const token = getToken();
  const response = await axios.get(
    `${API_BASE_URL}/courses/code/${courseCode}`,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data.data;
};

// Update course
export const updateCourse = async (courseId: string, data: UpdateCourseData): Promise<Course> => {
  const token = getToken();
  const response = await axios.put(
    `${API_BASE_URL}/courses/${courseId}`,
    data,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data.data;
};

// Delete course
export const deleteCourse = async (courseId: string): Promise<void> => {
  const token = getToken();
  await axios.delete(
    `${API_BASE_URL}/courses/${courseId}`,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
};

// Enroll in course
export const enrollInCourse = async (courseId: string, courseCode: string): Promise<Course> => {
  const token = getToken();
  const response = await axios.post(
    `${API_BASE_URL}/courses/${courseId}/enroll`,
    { courseCode },
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data.data;
};

// Get course topics
export const getCourseTopics = async (courseId: string): Promise<any[]> => {
  const token = getToken();
  const response = await axios.get(
    `${API_BASE_URL}/courses/${courseId}/topics`,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data.data;
};

// Get detailed course with enrolled students
export interface StudentInfo {
  _id: string;
  name: string;
  email: string;
  studentInfo?: {
    realName?: string;
    studentId?: string;
    college?: string;
    major?: string;
  };
}

export interface CourseWithStudents extends Course {
  enrolledStudentsDetails: StudentInfo[];
}

export const getCourseWithStudents = async (courseId: string): Promise<CourseWithStudents> => {
  const token = getToken();
  const response = await axios.get(
    `${API_BASE_URL}/courses/${courseId}`,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data.data;
};

// Get all courses with enrolled students for teacher dashboard
export const getCoursesWithStudents = async (): Promise<CourseWithStudents[]> => {
  const token = getToken();
  const response = await axios.get(
    `${API_BASE_URL}/courses?teacher=me`,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data.data;
};

// ==================== Chapter Management ====================

export interface CreateChapterData {
  title: string;
  description?: string;
  order?: number;
}

export const addChapter = async (courseId: string, data: CreateChapterData): Promise<Chapter> => {
  const token = getToken();
  const response = await axios.post(
    `${API_BASE_URL}/courses/${courseId}/chapters`,
    data,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data.data;
};

export const updateChapter = async (courseId: string, chapterId: string, data: Partial<CreateChapterData>): Promise<Chapter> => {
  const token = getToken();
  const response = await axios.put(
    `${API_BASE_URL}/courses/${courseId}/chapters/${chapterId}`,
    data,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data.data;
};

export const deleteChapter = async (courseId: string, chapterId: string): Promise<void> => {
  const token = getToken();
  await axios.delete(
    `${API_BASE_URL}/courses/${courseId}/chapters/${chapterId}`,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
};

// ==================== Section Management ====================

export const addSection = async (courseId: string, chapterId: string, data: CreateChapterData): Promise<Section> => {
  const token = getToken();
  const response = await axios.post(
    `${API_BASE_URL}/courses/${courseId}/chapters/${chapterId}/sections`,
    data,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data.data;
};

export const updateSection = async (courseId: string, chapterId: string, sectionId: string, data: Partial<CreateChapterData>): Promise<Section> => {
  const token = getToken();
  const response = await axios.put(
    `${API_BASE_URL}/courses/${courseId}/chapters/${chapterId}/sections/${sectionId}`,
    data,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data.data;
};

export const deleteSection = async (courseId: string, chapterId: string, sectionId: string): Promise<void> => {
  const token = getToken();
  await axios.delete(
    `${API_BASE_URL}/courses/${courseId}/chapters/${chapterId}/sections/${sectionId}`,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
};

// ==================== Topic Assignment ====================

export const addTopicsToChapter = async (courseId: string, chapterId: string, topicIds: string[]): Promise<Chapter> => {
  const token = getToken();
  const response = await axios.post(
    `${API_BASE_URL}/courses/${courseId}/chapters/${chapterId}/topics`,
    { topicIds },
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data.data;
};

export const addTopicsToSection = async (courseId: string, chapterId: string, sectionId: string, topicIds: string[]): Promise<Section> => {
  const token = getToken();
  const response = await axios.post(
    `${API_BASE_URL}/courses/${courseId}/chapters/${chapterId}/sections/${sectionId}/topics`,
    { topicIds },
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data.data;
};

export const removeTopicFromChapter = async (courseId: string, chapterId: string, topicId: string): Promise<void> => {
  const token = getToken();
  await axios.delete(
    `${API_BASE_URL}/courses/${courseId}/chapters/${chapterId}/topics/${topicId}`,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
};

export const removeTopicFromSection = async (courseId: string, chapterId: string, sectionId: string, topicId: string): Promise<void> => {
  const token = getToken();
  await axios.delete(
    `${API_BASE_URL}/courses/${courseId}/chapters/${chapterId}/sections/${sectionId}/topics/${topicId}`,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
};

export const reorderTopicsInChapter = async (courseId: string, chapterId: string, topicIds: string[]): Promise<Chapter> => {
  const token = getToken();
  const response = await axios.put(
    `${API_BASE_URL}/courses/${courseId}/chapters/${chapterId}/topics/reorder`,
    { topicIds },
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data.data;
};

export const reorderTopicsInSection = async (courseId: string, chapterId: string, sectionId: string, topicIds: string[]): Promise<Section> => {
  const token = getToken();
  const response = await axios.put(
    `${API_BASE_URL}/courses/${courseId}/chapters/${chapterId}/sections/${sectionId}/topics/reorder`,
    { topicIds },
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data.data;
};

// ==================== Draft and Status Management ====================

export const saveDraft = async (courseId: string, data: { title?: string; description?: string; chapters?: any[] }): Promise<Course> => {
  const token = getToken();
  const response = await axios.post(
    `${API_BASE_URL}/courses/${courseId}/save-draft`,
    data,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data.data;
};

export const updateCourseStatus = async (courseId: string, status: 'draft' | 'created' | 'published'): Promise<Course> => {
  const token = getToken();
  const response = await axios.put(
    `${API_BASE_URL}/courses/${courseId}/status`,
    { status },
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data.data;
};
